import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Pencil, X } from 'lucide-react';
import VendorAttachments from './VendorAttachments';

/**
 * Read-only vendor detail view. Shows all vendor fields in a labeled grid plus
 * the attachments list (with download available; upload/delete hidden via
 * the `readOnly` prop). Includes an "Edit" button so users can jump from
 * view mode into edit mode without closing.
 */
export default function VendorViewDialog({ open, onOpenChange, vendor, vendorTypeLabel, onEdit, canEdit }) {
  if (!vendor) return null;

  const F = ({ label, value, mono, className }) => (
    <div className={className || ''}>
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-sm ${mono ? 'font-mono' : ''} ${value ? 'text-slate-900' : 'text-slate-400'}`}>
        {value || '—'}
      </p>
    </div>
  );

  const addressLines = [
    vendor.address1,
    vendor.address2,
    [vendor.city, [vendor.state, vendor.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', '),
  ].filter(Boolean);

  const remitLines = vendor.remit_same_as_vendor
    ? ['(same as primary)']
    : [
        vendor.remit_address1,
        vendor.remit_address2,
        [vendor.remit_city, [vendor.remit_state, vendor.remit_zip].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      ].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="vendor-view-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle data-testid="vendor-view-title">{vendor.name}</DialogTitle>
              <DialogDescription>
                {vendor.winteam_vendor_number ? `WinTeam #${vendor.winteam_vendor_number} · ` : ''}
                <Badge variant={vendor.is_active ? 'default' : 'secondary'} className="ml-1">
                  {vendor.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </DialogDescription>
            </div>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                data-testid="vendor-view-edit-btn"
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          {/* Basics */}
          <section className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <F label="Vendor Type" value={vendorTypeLabel} />
            <F label="Payment Terms" value={vendor.payment_terms} />
            <F label="Default Payment Method" value={vendor.default_payment_method} />
          </section>

          {/* Contact */}
          <section className="border-t pt-4 space-y-3">
            <p className="text-sm font-semibold text-slate-700">Contact</p>
            <div className="grid grid-cols-2 gap-4">
              <F label="Contact Name" value={vendor.contact_name} />
              <F label="Attention" value={vendor.attention} />
              <F label="Email" value={vendor.contact_email} />
              <F label="Phone" value={vendor.contact_phone} mono />
            </div>
          </section>

          {/* Primary address */}
          <section className="border-t pt-4 space-y-2">
            <p className="text-sm font-semibold text-slate-700">Primary Address</p>
            {addressLines.length ? (
              addressLines.map((line, i) => (
                <p key={i} className="text-sm text-slate-900" data-testid={`vendor-view-address-line-${i}`}>{line}</p>
              ))
            ) : (
              <p className="text-sm text-slate-400">No address on file</p>
            )}
          </section>

          {/* Remittance */}
          <section className="border-t pt-4 space-y-2">
            <p className="text-sm font-semibold text-slate-700">Remittance / Pay-to Address</p>
            {remitLines.map((line, i) => (
              <p key={i} className="text-sm text-slate-900" data-testid={`vendor-view-remit-line-${i}`}>{line}</p>
            ))}
          </section>

          {/* AP */}
          <section className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">AP / Financial</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <F label="Issues 1099" value={vendor.is_1099 ? 'Yes' : 'No'} />
              <F label="Tax ID" value={vendor.tax_id} mono />
              <F label="1099 Type" value={vendor.type_of_1099} />
              <F label="Taxable" value={vendor.taxable ? 'Yes' : 'No'} />
              <F label="Lead Time" value={vendor.lead_time_days != null ? `${vendor.lead_time_days} day(s)` : null} />
              <F label="Collects Sales Tax" value={vendor.collects_sales_tax ? 'Yes' : 'No'} />
              <F label="Exemption Cert #" value={vendor.tax_exemption_cert} />
              <F label="Exemption Exp." value={vendor.exemption_expiration_date} />
            </div>
          </section>

          {/* Operational flags */}
          <section className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">Operational</p>
            <div className="grid grid-cols-2 gap-4">
              <F label="Contract on File?" value={vendor.contract_on_file ? 'Yes' : 'No'} />
              <F label="Has Approver?" value={vendor.has_approver ? 'Yes' : 'No'} />
            </div>
            {vendor.notes && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wider text-slate-500">Notes</p>
                <p className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="vendor-view-notes">{vendor.notes}</p>
              </div>
            )}
          </section>

          {/* Attachments — read-only mode (download allowed, no upload/delete) */}
          <section className="border-t pt-4">
            <VendorAttachments vendorId={vendor.id} readOnly />
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
